/**
 * Animator Controller documents (`*.controller.json`, `prism.controller/1`)
 * as edited by the Animator editor. Mirrors `prism_core::animator`
 * (engine/docs/systems/animation.md). Node positions live in the `editor`
 * block, which the engine ignores.
 */

export const CONTROLLER_FORMAT = 'prism.controller/1';
export const CONTROLLER_EXT = '.controller.json';

export type ParamType = 'float' | 'int' | 'bool' | 'trigger';
export const PARAM_TYPES: readonly ParamType[] = ['float', 'int', 'bool', 'trigger'];

export interface Param {
  name: string;
  type: ParamType;
  default?: number | boolean;
}

export type CondOp = 'greater' | 'less' | 'equals' | 'notEqual' | 'true' | 'false';

export interface Condition {
  param: string;
  op?: CondOp;
  value?: number;
}

export type Interruption = 'none' | 'source' | 'destination' | 'sourceThenDestination' | 'destinationThenSource';
export const INTERRUPTIONS: readonly Interruption[] = ['none', 'source', 'destination', 'sourceThenDestination', 'destinationThenSource'];

export interface Transition {
  from?: string;
  to: string;
  conditions?: Condition[];
  hasExitTime?: boolean;
  exitTime?: number;
  duration?: number;
  fixedDuration?: boolean;
  offset?: number;
  interruptionSource?: Interruption;
  orderedInterruption?: boolean;
  canTransitionToSelf?: boolean;
}

export type BlendType = '1d' | 'simpleDirectional2d' | 'freeformCartesian2d' | 'direct';
export const BLEND_TYPES: readonly BlendType[] = ['1d', 'simpleDirectional2d', 'freeformCartesian2d', 'direct'];

export interface BlendChild {
  motion: Motion;
  threshold?: number;
  position?: [number, number];
  timeScale?: number;
  mirror?: boolean;
  directParameter?: string;
}

export interface BlendTree {
  type: BlendType;
  parameter?: string;
  parameterY?: string;
  children: BlendChild[];
}

export type Motion = string | BlendTree;

export interface AnimEvent {
  time: number;
  function: string;
  args?: unknown;
}

export interface State {
  name: string;
  motion?: Motion;
  speed?: number;
  speedParameter?: string;
  mirror?: boolean;
  mirrorParameter?: string;
  cycleOffset?: number;
  cycleOffsetParameter?: string;
  loop?: boolean;
  transitions?: Transition[];
  events?: AnimEvent[];
  tag?: string;
}

export interface SubMachine {
  name: string;
  defaultState?: string;
  states: State[];
  subStateMachines?: SubMachine[];
}

export interface Layer {
  name: string;
  weight?: number;
  blending?: 'override' | 'additive';
  mask?: string[];
  syncedLayer?: string;
  syncedMotions?: Record<string, Motion>;
  defaultState?: string;
  states: State[];
  transitions?: Transition[];
  anyStateTransitions?: Transition[];
  subStateMachines?: SubMachine[];
}

export interface EditorLayout {
  /** layer name -> node key (state / machine path, `@any`, `@entry`, `@up`) -> [x, y]. */
  positions?: Record<string, Record<string, [number, number]>>;
}

export interface ControllerDoc {
  format: string;
  name?: string;
  parameters: Param[];
  layers: Layer[];
  editor?: EditorLayout;
}

export function newController(): ControllerDoc {
  return {
    format: CONTROLLER_FORMAT,
    parameters: [],
    layers: [{ name: 'Base Layer', states: [{ name: 'Idle' }] }],
  };
}

export function parseController(text: string): { doc: ControllerDoc | null; error: string | null } {
  if (!text.trim()) return { doc: newController(), error: null };
  try {
    const raw = JSON.parse(text) as Partial<ControllerDoc>;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { doc: null, error: 'not a JSON object' };
    const doc: ControllerDoc = {
      ...raw,
      format: raw.format ?? CONTROLLER_FORMAT,
      parameters: Array.isArray(raw.parameters) ? raw.parameters : [],
      layers: Array.isArray(raw.layers) ? raw.layers.map((l) => ({ ...l, states: Array.isArray(l.states) ? l.states : [] })) : [],
    };
    return { doc, error: null };
  } catch (e) {
    return { doc: null, error: e instanceof Error ? e.message : String(e) };
  }
}

export function serializeController(doc: ControllerDoc): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

// --- state machines -----------------------------------------------------

/** A machine inside a layer: the layer root (path []) or a sub-state machine. */
export interface Machine {
  states: State[];
  subStateMachines?: SubMachine[];
  defaultState?: string;
}

export function machineAt(layer: Layer, path: readonly string[]): Machine | null {
  let m: Machine = layer;
  for (const name of path) {
    const next: SubMachine | undefined = m.subStateMachines?.find((s) => s.name === name);
    if (!next) return null;
    m = next;
  }
  return m;
}

export function fullName(path: readonly string[], name: string): string {
  return [...path, name].join('/');
}

export interface StateRef {
  full: string;
  state: State;
  path: string[];
}

/** Every state of a layer with its full path. */
export function allStates(layer: Layer): StateRef[] {
  const out: StateRef[] = [];
  const walk = (m: Machine, path: string[]) => {
    for (const s of m.states) out.push({ full: fullName(path, s.name), state: s, path });
    for (const sub of m.subStateMachines ?? []) walk(sub, [...path, sub.name]);
  };
  walk(layer, []);
  return out;
}

/** Every sub-state machine path of a layer (`"A"`, `"A/B"`). */
export function allMachines(layer: Layer): string[] {
  const out: string[] = [];
  const walk = (m: Machine, path: string[]) => {
    for (const sub of m.subStateMachines ?? []) {
      const p = [...path, sub.name];
      out.push(p.join('/'));
      walk(sub, p);
    }
  };
  walk(layer, []);
  return out;
}

/** Resolves a transition endpoint name to a full state or machine path. */
export function resolveName(layer: Layer, name: string): string | null {
  const states = allStates(layer);
  if (states.some((s) => s.full === name)) return name;
  if (allMachines(layer).includes(name)) return name;
  const hits = states.filter((s) => s.full.split('/').pop() === name);
  return hits.length === 1 ? hits[0]!.full : null;
}

/** Where a transition is stored. */
export type TransitionLoc =
  | { kind: 'state'; state: string; index: number }
  | { kind: 'layer'; index: number }
  | { kind: 'any'; index: number };

export interface TransitionRef {
  loc: TransitionLoc;
  /** Full source path, or `@any`. */
  from: string;
  /** Resolved full destination path (or the raw name if unresolved). */
  to: string;
  t: Transition;
}

export function allTransitions(layer: Layer): TransitionRef[] {
  const out: TransitionRef[] = [];
  for (const s of allStates(layer)) {
    (s.state.transitions ?? []).forEach((t, index) =>
      out.push({ loc: { kind: 'state', state: s.full, index }, from: s.full, to: resolveName(layer, t.to) ?? t.to, t }),
    );
  }
  (layer.transitions ?? []).forEach((t, index) => {
    const any = t.from === 'any' || t.from === '*';
    out.push({ loc: { kind: 'layer', index }, from: any ? '@any' : (resolveName(layer, t.from ?? '') ?? t.from ?? ''), to: resolveName(layer, t.to) ?? t.to, t });
  });
  (layer.anyStateTransitions ?? []).forEach((t, index) =>
    out.push({ loc: { kind: 'any', index }, from: '@any', to: resolveName(layer, t.to) ?? t.to, t }),
  );
  return out;
}

export function stateByFull(layer: Layer, full: string): State | null {
  return allStates(layer).find((s) => s.full === full)?.state ?? null;
}

export function transitionAt(layer: Layer, loc: TransitionLoc): Transition | null {
  switch (loc.kind) {
    case 'state':
      return stateByFull(layer, loc.state)?.transitions?.[loc.index] ?? null;
    case 'layer':
      return layer.transitions?.[loc.index] ?? null;
    case 'any':
      return layer.anyStateTransitions?.[loc.index] ?? null;
  }
}

export function removeTransition(layer: Layer, loc: TransitionLoc): void {
  switch (loc.kind) {
    case 'state':
      stateByFull(layer, loc.state)?.transitions?.splice(loc.index, 1);
      break;
    case 'layer':
      layer.transitions?.splice(loc.index, 1);
      break;
    case 'any':
      layer.anyStateTransitions?.splice(loc.index, 1);
      break;
  }
}

/** A name not used by `taken`: `base`, `base 1`, `base 2`, ... */
export function uniqueName(base: string, taken: readonly string[]): string {
  if (!taken.includes(base)) return base;
  for (let i = 1; ; i++) {
    const n = `${base} ${i}`;
    if (!taken.includes(n)) return n;
  }
}

/** Renames a state or machine path (and everything under it) in every reference of the layer. */
export function renamePath(layer: Layer, oldFull: string, newFull: string): void {
  const fix = (name: string | undefined): string | undefined => {
    if (name === undefined) return name;
    const resolved = resolveName(layer, name) ?? name;
    if (resolved === oldFull) return newFull;
    if (resolved.startsWith(`${oldFull}/`)) return newFull + resolved.slice(oldFull.length);
    return name;
  };
  // Resolve before renaming (short names may become ambiguous after).
  const fixes: Array<() => void> = [];
  for (const r of allTransitions(layer)) {
    const t = r.t;
    const to = fix(t.to);
    const from = t.from && t.from !== 'any' && t.from !== '*' ? fix(t.from) : t.from;
    fixes.push(() => {
      t.to = to ?? t.to;
      if (from !== undefined) t.from = from;
    });
  }
  const walkDefaults = (m: Machine, path: string[]) => {
    if (m.defaultState) {
      const full = fullName(path, m.defaultState);
      if (full === oldFull) {
        const leaf = newFull.split('/').pop()!;
        fixes.push(() => (m.defaultState = leaf));
      }
    }
    for (const sub of m.subStateMachines ?? []) walkDefaults(sub, [...path, sub.name]);
  };
  walkDefaults(layer, []);
  for (const f of fixes) f();
}

/** Renames a parameter everywhere it is referenced. */
export function renameParam(doc: ControllerDoc, oldName: string, newName: string): void {
  const keys = ['param', 'parameter', 'parameterY', 'directParameter', 'speedParameter', 'mirrorParameter', 'cycleOffsetParameter'];
  const walk = (v: unknown) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      for (const k of Object.keys(o)) {
        if (keys.includes(k) && o[k] === oldName) o[k] = newName;
        else walk(o[k]);
      }
    }
  };
  walk(doc.layers);
  for (const p of doc.parameters) if (p.name === oldName) p.name = newName;
}

export function opsFor(type: ParamType | undefined): CondOp[] {
  switch (type) {
    case 'float':
      return ['greater', 'less'];
    case 'int':
      return ['greater', 'less', 'equals', 'notEqual'];
    case 'bool':
      return ['true', 'false'];
    default:
      return [];
  }
}

export function defaultCondition(p: Param | undefined): Condition {
  if (!p) return { param: '' };
  switch (p.type) {
    case 'float':
      return { param: p.name, op: 'greater', value: 0.1 };
    case 'int':
      return { param: p.name, op: 'equals', value: 0 };
    case 'bool':
      return { param: p.name, op: 'true' };
    default:
      return { param: p.name };
  }
}

export function conditionLabel(c: Condition, params: readonly Param[]): string {
  const p = params.find((x) => x.name === c.param);
  if (!p || p.type === 'trigger') return c.param || '?';
  if (p.type === 'bool') return c.op === 'false' ? `!${c.param}` : c.param;
  const sym: Record<string, string> = { greater: '>', less: '<', equals: '==', notEqual: '!=' };
  return `${c.param} ${sym[c.op ?? 'greater'] ?? c.op} ${c.value ?? 0}`;
}

export function motionLabel(m: Motion | undefined): string {
  if (m === undefined) return 'empty';
  if (typeof m === 'string') return m.replace(/^gltf:/, '').split('#').pop() || m;
  return `${m.type} tree (${m.children.length})`;
}
